import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../store/UserSlice";
import cn from "classnames";
import {
  Home,
  Users,
  FileText,
  Calendar,
  Settings,
  BarChart2,
  Briefcase,
  Tool,
  Database,
  Activity,
  ChevronDown,
  ListChecks,
  Factory,
  Building2,
  Fingerprint,
  CalendarDays,
  UserCircle,
  Clock4,
  CalendarRange,
  Palmtree,
  ClipboardList,
  TrendingUp,
  BarChart,
  Grid,
  Calculator,
  Upload,
  Receipt,
  Wrench,
  LayoutDashboard,
  Mail,
  BookA,
  SquareKanban,
  SquareDashedKanban,
  Gauge,
  Layers,
  FileSpreadsheet,
  Search,
  Bell,
  User,
  Menu,
  X,
  LogOut,
  AlertTriangle,
  Lightbulb,
  Warehouse,
  Package,
  PackageOpen,
  Truck,
  Cloud,
  Moon,
  Sun,
  TriangleAlert,
  RefreshCw
} from "lucide-react";
import { useTheme, Tooltip, Badge } from "@mui/material";
import { ColorModeContext, tokens } from "../../theme";
import { useContext } from "react";

const Navbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef({});
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const userState = useSelector((state) => state.user);
  const username = ((userState?.user?.firstName?.charAt(0) || "") + (userState?.user?.lastName?.charAt(0) || "")).trim();

  const handleMouseEnter = (key) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(key);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 250);
  };

  const handleDropdownMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 250);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate("/login");
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Close mobile menu when window resizes to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen]);

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef]);

  const navItems = {
    project: {
      label: "Auftrage",
      icon: <Briefcase className="w-4 h-4" />,
      items: [
        {
          label: "Dashboard",
          path: "/",
          icon: <LayoutDashboard className="w-4 h-4" />,
        },
        {
          label: "Auftrage ansehen",
          path: "/anrTable",
          icon: <ListChecks className="w-4 h-4" />,
        },
        {
          label: "Lager Auftrage ansehen",
          path: "stock/Auftrage",
          icon: <ListChecks className="w-4 h-4" />,
        },
        {
          label: "Auftrage erstellen",
          path: "/addanr",
          icon: <Factory className="w-4 h-4" />,
        },
        {
          label: "Google Sheet",
          path: "/GoogleSheet",
          icon: <FileSpreadsheet className="w-4 h-4" />,
        },
        {
          label: "Lieferant",
          path: "/LieferantTable",
          icon: <Building2 className="w-4 h-4" />,
        },
        {
          label: "Firma",
          path: "/FirmaTable",
          icon: <Building2 className="w-4 h-4" />,
        },
        {
          label: "Kunden",
          path: "/KundenPage",
          icon: <Building2 className="w-4 h-4" />,
        },
      ],
    },
    reklamation: {
      label: "Reklamation",
      icon: <AlertTriangle className="w-4 h-4" />,
      items: [
        {
          label: "Reklamation & Lernchance",
          path: "/reklamation",
          icon: <Lightbulb className="w-4 h-4" />,
        },
        {
          label: "Prozessoptimierung",
          path: "/ProcessImprovementPage",
          icon: <RefreshCw className="w-4 h-4" />,
        },
      ],
    },
    anfrage: {
      label: "Anfrage",
      icon: <BookA className="w-4 h-4" />,
      items: [
        {
          label: "Anfrage ansehen",
          path: "/AnfrageList",
          icon: <ListChecks className="w-4 h-4" />,
        },
        {
          label: "Anfrage erstellen",
          path: "/AnfrageForm",
          icon: <Factory className="w-4 h-4" />,
        },
      ],
    },
    aufgabe: {
      label: "Aufgabe",
      icon: <SquareKanban className="w-4 h-4" />,
      items: [
        {
          label: "Kanban",
          path: "/MainAufgabeBoard",
          icon: <SquareDashedKanban className="w-4 h-4" />,
        },
        {
          label: "EventsCalendar",
          path: "/EventsCalendar",
          icon: <Calendar className="w-4 h-4" />,
        },
      ],
    },
    Dashboard: {
      label: "Gewinn/Verlust",
      icon: <Gauge className="w-4 h-4" />,
      items: [
        {
          label: "Dashboard 1",
          path: "/ProfitLossDashboard2",
          icon: <LayoutDashboard className="w-4 h-4" />,
        },
        {
          label: "Dashboard 2",
          path: "/ProfitLossDashboard",
          icon: <Layers className="w-4 h-4" />,
        },
      ],
    },
    employee: {
      label: "Mitarbeiter",
      icon: <Users className="w-4 h-4" />,
      items: [
        {
          label: "Mitarbeiter ansehen",
          path: "/team",
          icon: <Users className="w-4 h-4" />,
        },
        {
          label: "Teilnahme",
          path: "/Attendance",
          icon: <Fingerprint className="w-4 h-4" />,
        },
        {
          label: "Mitarbeiter Calendar",
          path: "/AttendanceCalendar",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        {
          label: "Mitarbeiter",
          path: "/EmployeeView",
          icon: <UserCircle className="w-4 h-4" />,
        },
        {
          label: "Schicht hinzufügen",
          path: "/addShift",
          icon: <Clock4 className="w-4 h-4" />,
        },
        {
          label: "Schicht zuweisen",
          path: "/EmployeeShiftCalendar",
          icon: <CalendarRange className="w-4 h-4" />,
        },
        {
          label: "Urlaub Planner",
          path: "/UrlaubPlanner",
          icon: <Palmtree className="w-4 h-4" />,
        },
        {
          label: "Mitarbeiterkompetenzmatrix",
          path: "/EmployeeSkillsMatrix",
          icon: <Grid className="w-4 h-4" />,
        },
      ],
    },
    reports: {
      label: "Berichte",
      icon: <FileText className="w-4 h-4" />,
      items: [
        {
          label: "Management Logs",
          path: "/managementLogs",
          icon: <ClipboardList className="w-4 h-4" />,
        },
        {
          label: "Logged In Users",
          path: "/logged-in-users",
          icon: <Users className="w-4 h-4" />,
        },
        {
          label: "UberStundens",
          path: "/UberStundens",
          icon: <TrendingUp className="w-4 h-4" />,
        },
        {
          label: "Projektbericht",
          path: "/mainaggregation",
          icon: <BarChart className="w-4 h-4" />,
        },
        {
          label: "Projektstunden",
          path: "/AttendanceReport",
          icon: <BarChart2 className="w-4 h-4" />,
        },
        {
          label: "Finalcalculations",
          path: "/Finalcalculations",
          icon: <Activity className="w-4 h-4" />,
        },
        {
          label: "Erlößbetrachtung",
          path: "/RechnungMatcher",
          icon: <Activity className="w-4 h-4" />,
        },
      ],
    },
    accounting: {
      label: "Buchhaltung",
      icon: <Database className="w-4 h-4" />,
      items: [
        {
          label: "Materialrechnungen einsehen",
          path: "/Rechnung",
          icon: <Calculator className="w-4 h-4" />,
        },
        {
          label: "Materialrechnungen hinzufügen",
          path: "/AddRechnungs",
          icon: <Receipt className="w-4 h-4" />,
        },
        {
          label: "Invoices Balances",
          path: "/invoices",
          icon: <Receipt className="w-4 h-4" />,
        },
        // {
        //   label: "Rechnungsverwaltung",
        //   path: "/invoice-dashboard",
        //   icon: <Receipt className="w-4 h-4" />,
        // },
        {
          label: "Lexware Invoicing",
          path: "/Lexware",
          icon: <Receipt className="w-4 h-4" />,
        },
      ],
    },
    stock: {
      label: "Lagerverwaltung",
      icon: <Warehouse className="w-4 h-4" />,
      items: [
        {
          label: "Lager Dashboard",
          path: "/stock",
          icon: <Package className="w-4 h-4" />,
        },
        {
          label: "Produkte",
          path: "/stock/products",
          icon: <PackageOpen className="w-4 h-4" />,
        },
        {
          label: "Materialbewegungen",
          path: "/stock/movements",
          icon: <Truck className="w-4 h-4" />,
        },
        {
          label: "Odoo Konfiguration",
          path: "/stock/odoo-config",
          icon: <Cloud className="w-4 h-4" />,
        },
        {
          label: "Einfache App Verwaltung",
          path: "/simple-app-admin",
          icon: <Settings className="w-4 h-4" />,
        },
      ],
    },
    planning: {
      label: "Projektplanung",
      icon: <Calendar className="w-4 h-4" />,
      items: [
        {
          label: "Vorlagenersteller",
          path: "/TemplateMaker",
          icon: <Wrench className="w-4 h-4" />,
        },
        {
          label: "Projektplaner",
          path: "/TemplateList",
          icon: <Calendar className="w-4 h-4" />,
        },
        {
          label: "Interactive Planner",
          path: "/InteractivePlanningCalendar",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        // {
        //   label: "Planner2",
        //   path: "/Planner2",
        //   icon: <CalendarDays className="w-4 h-4" />,
        // },
        {
          label: "Machine Matrix",
          path: "/EmployeeMachineMatrix",
          icon: <Grid className="w-4 h-4" />,
        },
      ],
    },
    orders: {
      label: "Bestellung",
      icon: <Mail className="w-4 h-4" />,
      items: [
        {
          label: "Bestellung",
          path: "/Bestallung",
          icon: <Wrench className="w-4 h-4" />,
        },
        {
          label: "Bestellung Ansehen",
          path: "/ViewBestallungs",
          icon: <Calendar className="w-4 h-4" />,
        },
        {
          label: "Email",
          path: "/EmailInbox",
          icon: <Mail className="w-4 h-4" />,
        },
      ],
    },
    users: {
      label: "Benutzer",
      icon: <User className="w-4 h-4" />,
      items: [
        {
          label: "Benutzerboard",
          path: "/users",
          icon: <Wrench className="w-4 h-4" />,
        },
        {
          label: "Form Customization",
          path: "/custom-form",
          icon: <Wrench className="w-4 h-4" />,
        },
      ],
    },
  };

  // Quick Access Tabs for Bottom Navigation with custom images
  const quickAccessTabs = [
    {
      path: "/MainCalendar2",
      label: "Kalender",
      imgSrc: "./assets/timetable.png",
    },
    { path: "/team", label: "Team", imgSrc: "./assets/work-time.png" },
    {
      path: "/addanr",
      label: "Projekt",
      imgSrc: "./assets/project-management.png",
    },
    {
      path: "/anrTable",
      label: "Aufgaben",
      imgSrc: "./assets/task-management.png",
    },
    { path: "/", label: "Dashboard", icon: <Home className="w-5 h-5" /> },
  ];

  return (
    <>
      {/* Top Navigation */}
      <nav className={`${theme.palette.mode === 'dark' ? 'bg-[#1F2A40]' : 'bg-white'} shadow-md fixed w-full z-50`}>
        <div className="mx-auto px-4">
          <div className="flex justify-between h-16">
            {/* Logo and Mobile Menu Button */}
            <div className="flex items-center">
              <button
                className={`inline-flex items-center justify-center p-2 rounded-md ${theme.palette.mode === 'dark' ? 'text-gray-200' : 'text-gray-700'} lg:hidden transition-all duration-300 ease-in-out`}
                onClick={toggleMobileMenu}
              >
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6 transform transition-transform duration-300 ease-in-out rotate-180" />
                ) : (
                  <Menu className="block h-6 w-6 transform transition-transform duration-300 ease-in-out" />
                )}
              </button>

              <Link
                to="/"
                className={`flex items-center px-4 ${theme.palette.mode === 'dark' ? 'text-gray-200' : 'text-gray-800'} hover:text-blue-600`}
              >
                <LayoutDashboard className="w-6 h-6 text-blue-600" />
                <span className="ml-2 font-semibold text-xl tracking-tight">
                  GABS
                </span>
              </Link>
            </div>

            {/* Desktop Navigation Menu */}
            <div className="hidden lg:flex lg:items-center lg:justify-center flex-1">
              <div className="flex items-center space-x-4">
                {Object.entries(navItems).map(
                  ([key, { label, icon, items }]) => (
                    <div
                      key={key}
                      className="relative flex items-center"
                      onMouseEnter={() => handleMouseEnter(key)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button className={`flex group items-center px-3 py-2 text-sm font-medium ${theme.palette.mode === 'dark' ? 'text-gray-200 hover:text-blue-400' : 'text-gray-700 hover:text-blue-600'} rounded-md ${theme.palette.mode === 'dark' ? 'hover:bg-[#3e4396]' : 'hover:bg-blue-50'} transition-all duration-200`}>
                        {icon}
                        <span
                          className={cn(
                            "ml-2 transition-all duration-300 ease-in-out",
                            {
                              "inline opacity-100 translate-x-0":
                                activeDropdown === key,
                              "hidden group-hover:inline opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-2":
                                activeDropdown !== key,
                            }
                          )}
                        >
                          {label}
                        </span>
                        <ChevronDown className="w-4 h-4 ml-1 opacity-70" />
                      </button>

                      {activeDropdown === key && (
                        <div
                          className={`absolute top-full left-0 w-64 mt-1 ${theme.palette.mode === 'dark' ? 'bg-[#1F2A40]' : 'bg-white'} rounded-md shadow-lg z-50 border ${theme.palette.mode === 'dark' ? 'border-gray-700' : 'border-gray-100'} overflow-hidden`}
                          ref={(el) => (dropdownRef.current[key] = el)}
                          onMouseEnter={handleDropdownMouseEnter}
                          onMouseLeave={handleDropdownMouseLeave}
                        >
                          <div className="py-1">
                            {items.map((item, index) => (
                              <Link
                                key={index}
                                to={item.path}
                                className={`flex items-center px-4 py-2 text-sm ${theme.palette.mode === 'dark' ? 'text-gray-200 hover:bg-[#3e4396] hover:text-blue-400' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                              >
                                {item.icon}
                                <span className="ml-2">{item.label}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Right Side - User Controls */}
            <div className="flex items-center">
              <button className={`p-2 rounded-full ${theme.palette.mode === 'dark' ? 'text-gray-200 hover:text-gray-100 hover:bg-[#3e4396]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} onClick={colorMode.toggleColorMode}>
                {theme.palette.mode === "dark" ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </button>
              <button className={`p-2 rounded-full ${theme.palette.mode === 'dark' ? 'text-gray-200 hover:text-gray-100 hover:bg-[#3e4396]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} onClick={() => navigate("/reklamation")}>
                <Tooltip title="Reklamationen">
                  <Badge badgeContent={3} color="error">
                    <TriangleAlert className="w-5 h-5" />
                  </Badge>
                </Tooltip>
                </button>
              {/* <button className="p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 ml-2 relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button> */}
              <div className="relative" ref={userMenuRef}>
              <button
                onClick={toggleUserMenu}
                className={`p-2 rounded-full ml-2 ${
                  theme.palette.mode === 'dark'
                    ? 'text-gray-200 hover:text-gray-100 hover:bg-[#3e4396]'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-300 text-blue-700 flex items-center justify-center text-base font-extrabold shadow-md">
                  {username}
                </div>
              </button>


                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className={`absolute right-0 mt-2 w-48 ${theme.palette.mode === 'dark' ? 'bg-[#1F2A40]' : 'bg-white'} rounded-md shadow-lg py-1 z-50 border ${theme.palette.mode === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                    <Link
                      to="/profile"
                      className={`flex items-center px-4 py-2 text-sm ${theme.palette.mode === 'dark' ? 'text-gray-200 hover:bg-[#3e4396] hover:text-blue-400' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                    >
                      <UserCircle className="w-4 h-4 mr-2" />
                      Profil
                    </Link>
                    <Link
                      to="/settings"
                      className={`flex items-center px-4 py-2 text-sm ${theme.palette.mode === 'dark' ? 'text-gray-200 hover:bg-[#3e4396] hover:text-blue-400' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Einstellungen
                    </Link>
                    <hr className="my-1 border-gray-200" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Abmelden
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed top-16 inset-x-0 h-[calc(100vh-4rem)] z-40 bg-white overflow-y-auto flex flex-col">
          <div className="px-2 pt-2 pb-3 space-y-1 border-t border-gray-200 flex-grow">
            {Object.entries(navItems).map(([key, { label, icon, items }]) => (
              <div key={key} className="py-1">
                <div className="flex items-center px-3 py-2 text-sm font-medium text-gray-800">
                  {icon}
                  <span className="ml-2">{label}</span>
                </div>
                <div className="pl-10 space-y-1">
                  {items.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className="flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-md"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.icon}
                      <span className="ml-2">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {/* Logout option in mobile menu */}
            <div className="border-t border-gray-200 mt-2 pt-2">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span>Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Space to prevent content from being hidden behind the fixed bottom nav */}
      <div className="h-16 w-full"></div>
    </>
  );
};

export default Navbar;
